import { NativeState, NativeStateType } from '../models/native-state';
import { Episode, EpisodeCompoundId, PodcastId } from '../models/podcasts';
import debugLog from '../utils/debug';
import { reportSentryWarning } from '../utils/error-handling';
import { getInitialValue, setLazyValue } from '../utils/localstorage';
import { isNativeAppWithStateV1 } from '../utils/platform';
import { objectKeys } from '../utils/ts-extras';
import {
  getNativeState,
  getNativeStateFromLastSession,
  nativeStateUpdateCount,
} from './native-app-state';
import { idToEpisode } from './podcasts';
import { trackAdSkipUnlessRecent, usageActions } from './usage-data';
import { currentUserId } from './user';
import { setEpisodeProgress, useUserDataStore } from './user-data';

//
// Capture usage from native state and pass it to UsageStore,
// clearing it from native state to avoid double-counting
//

const log = (...args: any[]) => {
  debugLog({ logName: 'native-state', logColor: 'gold' }, ...args);
};

type NativeUsageHandlers = {
  acknowledgeAdSkipsUpTo: (timestampMs: number) => void;
  acknowledgeUsageData: (payload: {
    secondsListened?: Record<EpisodeCompoundId, number>;
    episodeCompletions?: Record<EpisodeCompoundId, number>;
  }) => void;
};

// Not defined initially to avoid circular dependency
let nativeUsageHandlers: NativeUsageHandlers | null = null;
export function initNativeUsage(handlers: NativeUsageHandlers) {
  nativeUsageHandlers = handlers;
  const currentNativeState = getNativeState();
  if (currentNativeState && currentNativeState.source !== 'initial') {
    consumeNativeUsageFromPlayer('current');
  } else {
    consumeNativeUsageFromPlayer('last-session');
  }
}

if (typeof window !== 'undefined')
  addEventListener('USAGE_SYNC_ABOUT_TO_START', () =>
    consumeNativeUsageFromPlayer('current')
  );

let lastNativeAdSkipAttemptAt = getInitialValue(
  'UsageStore__lastNativeAdSkipAttemptAt',
  (val) => Number.isInteger(val) && (val as number) >= 0,
  0
);

function setLastNativeAdSkipAttemptAt(value: number) {
  lastNativeAdSkipAttemptAt = value;
  setLazyValue('UsageStore__lastNativeAdSkipAttemptAt', value);
}

export function consumeNativeUsageFromPlayer(type: NativeStateType) {
  const nativeState =
    type === 'current' ? getNativeState() : getNativeStateFromLastSession();
  if (!nativeState) return;

  consumeNativeUsageFromState(nativeState, type);
}

export function consumeNativeUsageFromState(
  nativeState: NativeState,
  source: NativeStateType,
  { shouldConsumeMinutes = true }: { shouldConsumeMinutes?: boolean } = {}
) {
  if ((nativeState.userId || null) !== currentUserId.value) {
    // The native app is initially not associated with any user, so this is expected
    // initially when the frontend is signed in.
    if (!nativeState.userId && nativeStateUpdateCount <= 1) return;

    reportSentryWarning(new Error('Ignoring native usage for different user'), {
      nativeUserId: nativeState.userId,
      currentUserId: currentUserId.value,
      source,
      nativeState,
      nativeStateUpdateCount,
    });
    return;
  }

  consumeNativeAdSkipsFromState(nativeState, source);
  consumeNativeCompletionsFromState(nativeState, source);
  if (shouldConsumeMinutes) consumeNativeMinutesFromState(nativeState, source);
}

function consumeNativeAdSkipsFromState(
  nativeState: NativeState,
  source: NativeStateType
) {
  const adSkips = nativeState.untrackedAdSkips;
  if (!adSkips.length) return;

  // Use a single timestamp cursor instead of tracking individual processed skips.
  // We only need to know which skips are new since the last time we consumed
  // native state, and Android can clear everything up to that point.
  const newAdSkips = adSkips.filter(
    (skip) => skip.skipAttemptedAt > lastNativeAdSkipAttemptAt
  );

  // Typically all ad skips should be processed. It's rare that any will be outdated.
  if (newAdSkips.length === adSkips.length) {
    log('Tracking', newAdSkips.length, 'native ad-skips', newAdSkips);
  } else {
    log('Tracking only', newAdSkips.length, 'of', adSkips.length, 'native ad-skips', {
      new: newAdSkips,
      outdated: adSkips.filter((skip) => !newAdSkips.includes(skip)),
    });
  }

  newAdSkips.forEach((skip) => {
    if (skip.skipFailedReason == null) {
      trackAdSkipUnlessRecent(skip.episodeId, skip.startTime, skip.endTime);
    }
  });

  const maxAttemptAt = newAdSkips.reduce(
    (max, skip) => Math.max(max, skip.skipAttemptedAt),
    lastNativeAdSkipAttemptAt
  );

  if (maxAttemptAt > lastNativeAdSkipAttemptAt) {
    setLastNativeAdSkipAttemptAt(maxAttemptAt);
    if (source === 'current') nativeUsageHandlers?.acknowledgeAdSkipsUpTo(maxAttemptAt);
  } else if (
    adSkips.some((skip) => skip.skipAttemptedAt <= lastNativeAdSkipAttemptAt)
  ) {
    // Clear stale native ad-skips that were already processed.
    if (source === 'current')
      nativeUsageHandlers?.acknowledgeAdSkipsUpTo(lastNativeAdSkipAttemptAt);
  }
}

function consumeNativeCompletionsFromState(
  nativeState: NativeState,
  source: NativeStateType
) {
  const completionsToAcknowledge: Record<EpisodeCompoundId, number> = {};
  const untrackedCompletions = nativeState.untrackedCompletions || {};

  objectKeys(untrackedCompletions).forEach((episodeId) => {
    const completedAt = untrackedCompletions[episodeId];

    const episode = idToEpisode(episodeId);
    const existingProgress = useUserDataStore.getState().episodesProgress[episodeId];
    const duration =
      episode?.duration ||
      (existingProgress ? existingProgress[0] + existingProgress[1] : 0);
    const episodeForProgress = episode || ({ id: episodeId } as Episode);

    setEpisodeProgress(episodeForProgress, duration, duration, completedAt);
    completionsToAcknowledge[episodeId] = completedAt;
  });

  if (Object.keys(completionsToAcknowledge).length) {
    // Only acknowledge current-session usage. Last-session state is used for
    // recovery, but we leave it untouched instead of mutating historical state.
    if (source !== 'current') return;
    nativeUsageHandlers?.acknowledgeUsageData({
      episodeCompletions: completionsToAcknowledge,
    });
  }
}

function consumeNativeMinutesFromState(
  nativeState: NativeState,
  source: NativeStateType
) {
  if (!isNativeAppWithStateV1) return;

  const secondsToAcknowledge: Record<EpisodeCompoundId, number> = {};
  const currentEpisodeId = nativeState.episodes[0]?.id;

  objectKeys(nativeState.untrackedSeconds).forEach((episodeId) => {
    const seconds = nativeState.untrackedSeconds[episodeId];
    if (!(seconds >= 0)) return;

    const isCurrentEpisode = episodeId === currentEpisodeId;
    const minutesToAdd = Math.floor(seconds / 60);

    if (minutesToAdd > 0) {
      const podcastId = episodeId.split('|')[0] as PodcastId;
      usageActions.addMinutesListened(podcastId, minutesToAdd);
    }

    if (isCurrentEpisode) {
      // Keep the current episode's sub-minute remainder in native state so it
      // can continue accumulating toward the next coarse analytics minute.
      const secondsToAck = minutesToAdd * 60;
      if (secondsToAck > 0) secondsToAcknowledge[episodeId] = secondsToAck;
    } else {
      // For non-current episodes, clear everything to keep native state lean.
      // We still only record whole minutes in analytics, but we don't need to
      // preserve leftover seconds for episodes that are no longer active.
      secondsToAcknowledge[episodeId] = seconds;
    }
  });

  if (Object.keys(secondsToAcknowledge).length) {
    // Only acknowledge current-session usage. Last-session state is used for
    // recovery, but we leave it untouched instead of mutating historical state.
    if (source !== 'current') return;
    nativeUsageHandlers?.acknowledgeUsageData({
      secondsListened: secondsToAcknowledge,
    });
  }
}
